import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  FormControl, 
  InputLabel,
  Select, 
  MenuItem, 
  RadioGroup, 
  Radio, 
  FormControlLabel, 
  TextField,
  FormHelperText,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { offices, subOffices, services } from '../data/formData';

const ServiceAvailedStep = ({ 
  InternalExternal, setInternalExternal,
  office, setOffice,
  subOffice, setSubOffice,
  service, setService,
  otherSpecification, setOtherSpecification,
  errors,
  themeColors 
}) => {
  const isOthersSelected = service && service.includes('_others');
  const hasSubOffices = office && subOffices[office] && subOffices[office].length > 0;
  
  // Get the current available services based on office/subOffice selection
  const getCurrentServices = () => {
    if (subOffice && services[subOffice]) {
      return services[subOffice];
    } else if (office && services[office]) {
      return services[office];
    }
    return [];
  };
  
  // Get the name of the selected office
  const getSelectedOfficeName = () => {
    const selectedOffice = offices.find(o => o.value === office);
    return selectedOffice ? selectedOffice.label : '';
  };
  
  // Get the name of the selected sub-office
  const getSelectedSubOfficeName = () => {
    if (!subOffice || !office || !subOffices[office]) return '';
    const selectedSubOffice = subOffices[office].find(so => so.value === subOffice);
    return selectedSubOffice ? selectedSubOffice.label : '';
  };

  // Get the name of the selected service
  const getSelectedServiceName = () => {
    const currentServices = getCurrentServices();
    const selectedService = currentServices.find(s => s.value === service);
    return selectedService ? selectedService.label : '';
  };

  // Handle client type change
  const handleInternalExternalChange = (event, newInternalExternal) => {
    console.log('Setting InternalExternal to:', newInternalExternal);
    if (newInternalExternal !== null) {
      setInternalExternal(newInternalExternal);
      // Reset selections when client type changes
      setOffice('');
      setSubOffice('');
      setService('');
      setOtherSpecification('');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ 
        fontSize: { xs: '1rem', sm: '1.25rem' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-start' },
        mb: 3
      }}>
        Service Availed
        <Tooltip title="Select the office and service you transacted with" placement="top">
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>
      
      {/* Client Type Selection */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 4,
          backgroundColor: '#fff', 
          border: `1px solid ${themeColors.borderColor}`,
          borderRadius: 1
        }}
      >
        <Typography fontWeight={500} gutterBottom sx={{ 
          mb: 2,
          color: themeColors.primary
        }}>
          Client Type:
        </Typography>
        
        <FormControl 
          fullWidth 
          sx={{ mb: 3 }}
          error={errors?.InternalExternal}
        >
          <ToggleButtonGroup
            value={InternalExternal}
            exclusive
            onChange={handleInternalExternalChange}
            aria-label="client type"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                border: `1px solid ${themeColors.borderColor}`,
                p: 2,
                '&.Mui-selected': {
                  backgroundColor: themeColors.bgLight,
                  borderColor: themeColors.primary,
                  color: themeColors.primary,
                  '&:hover': {
                    backgroundColor: themeColors.bgLight,
                  }
                },
                '&:hover': {
                  backgroundColor: 'rgba(128, 0, 0, 0.04)'
                }
              }
            }}
          >
            <ToggleButton value="Internal Client" aria-label="internal client">
              <BusinessIcon sx={{ mr: 1 }} />
              <Typography>Internal Client</Typography>
            </ToggleButton>
            <ToggleButton value="External Client" aria-label="external client">
              <PersonIcon sx={{ mr: 1 }} />
              <Typography>External Client</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          {errors?.InternalExternal && <FormHelperText>Please select a client type</FormHelperText>}
        </FormControl>
        
        {/* Selected Client Type Info */}
        {InternalExternal && (
          <Box sx={{ 
            mt: 1, 
            p: 2, 
            backgroundColor: themeColors.bgLight,
            borderRadius: 1
          }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Selected Client Type:
            </Typography>
            <Typography variant="body2">
              {InternalExternal}
            </Typography>
          </Box>
        )}
      </Paper>
      
      {/* Only show office selection when client type is selected */}
      <Collapse in={!!InternalExternal} timeout={300}>
        <Paper 
          elevation={1} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 4,
            backgroundColor: '#fff', 
            border: `1px solid ${themeColors.borderColor}`,
            borderRadius: 1
          }}
        >
          <Grid container spacing={3}>
            {/* Office Selection Section */}
            <Grid item xs={12} md={5}>
              <Typography fontWeight={500} gutterBottom sx={{ 
                mb: 2,
                color: themeColors.primary
              }}>
                Office you have transacted with:
              </Typography>
              
              <FormControl 
                fullWidth 
                sx={{ mb: 3 }}
                error={errors?.office}
              >
                <InputLabel id="office-select-label">Select Office</InputLabel>
                <Select
                  labelId="office-select-label"
                  id="office-select"
                  value={office}
                  label="Select Office"
                  onChange={(e) => {
                    setOffice(e.target.value);
                    setSubOffice('');
                    setService('');
                    setOtherSpecification('');
                  }}
                  sx={{
                    mb: 2,
                    '&.MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: themeColors.primary,
                      },
                    },
                  }}
                >
                  {offices.map((officeOption) => (
                    <MenuItem key={officeOption.value} value={officeOption.value}>
                      {officeOption.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors?.office && <FormHelperText>Please select an office</FormHelperText>}
              </FormControl>
              
              {/* Sub-office selection (if applicable) */}
              {office && hasSubOffices && (
                <Collapse in={true} timeout={300}>
                  <FormControl 
                    fullWidth 
                    sx={{ mb: 2 }}
                    error={errors?.subOffice}
                    required
                  >
                    <InputLabel id="sub-office-select-label">Select Sub-Office</InputLabel>
                    <Select
                      labelId="sub-office-select-label"
                      id="sub-office-select"
                      value={subOffice}
                      label="Select Sub-Office"
                      onChange={(e) => {
                        setSubOffice(e.target.value);
                        setService('');
                        setOtherSpecification('');
                      }}
                      sx={{
                        '&.MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: themeColors.primary,
                          },
                        },
                      }}
                    >
                      {subOffices[office].map((subOfficeOption) => (
                        <MenuItem key={subOfficeOption.value} value={subOfficeOption.value}>
                          {subOfficeOption.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors?.subOffice && (
                      <FormHelperText>
                        {hasSubOffices ? "Please select a sub-office" : ""}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Collapse>
              )}
              
              {/* Selected Office/Sub-Office Summary */}
              {office && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: themeColors.bgLight,
                  borderRadius: 1
                }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Selected Office:
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    {getSelectedOfficeName()}
                  </Typography>
                  
                  {subOffice && (
                    <>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Selected Sub-Office:
                      </Typography>
                      <Typography variant="body2">
                        {getSelectedSubOfficeName()}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Grid>
            
            {/* Service Selection Section */}
            <Grid item xs={12} md={7}>
              <Typography fontWeight={500} gutterBottom sx={{ 
                mb: 2,
                color: themeColors.primary
              }}>
                Service you have availed:
              </Typography>
              
              {/* Only show services when either office or sub-office is selected 
                  For offices with sub-offices, require sub-office selection first */}
              {((hasSubOffices && subOffice && services[subOffice]) || 
                (office && services[office] && !hasSubOffices)) ? (
                <FormControl 
                  fullWidth
                  error={errors?.service}
                >
                  <RadioGroup
                    value={service}
                    onChange={(e) => {
                      setService(e.target.value);
                      if (!e.target.value.includes('_others')) {
                        setOtherSpecification('');
                      }
                    }}
                  >
                    {getCurrentServices().map((serviceOption) => (
                      <FormControlLabel
                        key={serviceOption.value}
                        value={serviceOption.value}
                        control={
                          <Radio 
                            sx={{
                              color: themeColors.borderColor,
                              '&.Mui-checked': {
                                color: themeColors.primary,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ 
                            fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                            fontWeight: service === serviceOption.value ? 500 : 400
                          }}>
                            {serviceOption.label}
                          </Typography>
                        }
                        sx={{ 
                          border: `1px solid ${errors?.service ? '#f44336' : themeColors.borderColor}`,
                          borderRadius: 1,
                          mb: 1,
                          p: 1,
                          width: '100%',
                          backgroundColor: service === serviceOption.value ? themeColors.bgLight : 'transparent',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: service === serviceOption.value 
                              ? themeColors.bgLight 
                              : 'rgba(128, 0, 0, 0.04)'
                          }
                        }}
                      />
                    ))}
                  </RadioGroup>
                  
                  {errors?.service && (
                    <FormHelperText sx={{ mb: 2 }}>
                      Please select a service
                    </FormHelperText>
                  )}
                  
                  {/* Additional text field for "Others" option */}
                  {isOthersSelected && (
                    <Collapse in={true} timeout={300}>
                      <TextField
                        fullWidth
                        label="Please specify the service"
                        variant="outlined"
                        value={otherSpecification}
                        onChange={(e) => setOtherSpecification(e.target.value)}
                        error={errors?.otherSpecification}
                        helperText={errors?.otherSpecification ? "Please specify the service" : ""}
                        required
                        sx={{
                          mt: 2,
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                              borderColor: themeColors.primary,
                            },
                          },
                        }}
                      />
                    </Collapse>
                  )}
                  
                  {/* Selected Service Summary */}
                  {service && (
                    <Box sx={{ 
                      mt: 3, 
                      p: 2, 
                      backgroundColor: themeColors.bgLight,
                      borderRadius: 1,
                      display: { xs: 'block', md: 'none' } // Only show on mobile
                    }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Selected Service:
                      </Typography>
                      <Typography variant="body2">
                        {getSelectedServiceName()}
                        {isOthersSelected && otherSpecification && `: ${otherSpecification}`}
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              ) : (
                // Show instructions when no office is selected or when office has no services
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1,
                  border: `1px dashed ${themeColors.borderColor}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" color="textSecondary">
                    {!office ? "Please select an office first to see available services" : 
                     hasSubOffices ? "Please select a sub-office to see available services" : 
                     "No services available for this office"}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ServiceAvailedStep;